import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { window } from "browser-monads"
import { formatVars } from "../helpers/translation"
import ConfirmationDialog from "./ConfirmationDialog"
import Paragraph from "./Paragraph"
import { SimpleLink } from "./TxlLink"
import List from "./styled/List"

const navigator = window.navigator

const DownloadHelpUnzip = ({ unzipProgram }) => {
  const { t } = useTranslation("generic")
  return (
    <div>
      <Paragraph>
        <span>{t("##.Download.HelpUnzip")}</span>
        {unzipProgram && (
          <span>
            {" "}
            <span>{t("##.Download.HelpUnzipProgram")}</span>
            <SimpleLink target="_blank" rel="noopener" href={unzipProgram.url}>
              {unzipProgram.name}
            </SimpleLink>
          </span>
        )}
      </Paragraph>
      <Paragraph>{t("##.Download.HelpPassword")}</Paragraph>
    </div>
  )
}

const DownloadHelpIosUnzip = ({ syncApp, extractApp }) => {
  const { t } = useTranslation("generic")
  return (
    <div>
      <Paragraph>{t("##.Download.HelpIosZip")}</Paragraph>
      <List>
        <li>
          <span>{t("##.Download.HelpIosZipOptionSync")}</span>
          {" "}
          <SimpleLink target="_blank" rel="noopener" href={syncApp.url}>
            {syncApp.name}
          </SimpleLink>
        </li>
        <li>
          <span>{t("##.Download.HelpIosZipOptionExtract")}</span>
          {" "}
          <SimpleLink target="_blank" href={extractApp.url} rel="noopener">
            {extractApp.name}
          </SimpleLink>
        </li>
      </List>
      <Paragraph>{t("##.Download.HelpIosZip2")}</Paragraph>
    </div>
  )
}

function isIOS() {
  return /iP(hone|od|ad)/.test(navigator.platform)
}

function getModalTitle() {
  if (isIOS()) {
    return "##.Download.HelpIosZipHeader"
  }
  return "##.Download.HelpUnzipHeader"
}

function determineOperatingSystem() {
  const { appVersion } = navigator
  if (appVersion.indexOf("Win") !== -1) return "windows"
  if (appVersion.indexOf("Max") !== -1) return "macos"
  if (appVersion.indexOf("X11") !== -1) return "linux"
  if (appVersion.indexOf("Linux") !== -1) return "linux"
  return "unkown"
}

function getUnzipProgram() {
  const os = determineOperatingSystem()

  switch (os) {
    case "windows":
      return { name: "7-Zip", url: "https://www.7-zip.org/" }
    case "macos":
      return {
        name: "The Unarchiver",
        url: "https://itunes.apple.com/app/the-unarchiver/id425424353",
      }
    case "linux":
      return { name: "File Roller", url: "http://fileroller.sourceforge.net" }
    default:
      return null
  }
}

function getModalBody() {
  if (isIOS()) {
    const props = {
      syncApp: {
        name: "Dropbox",
        url: "https://itunes.apple.com/app/dropbox/id327630330",
      },
      extractApp: {
        name: "iZip",
        url:
          "https://itunes.apple.com/app/izip-zip-unzip-unrar-tool/id413971331",
      },
    }
    return <DownloadHelpIosUnzip {...props} />
  }
  return <DownloadHelpUnzip unzipProgram={getUnzipProgram()} />
}

const DownloadHelpDialog = ({ fileCount, ...rest }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation("generic")

  const tWithFileCount = useCallback(
    translationKey =>
      formatVars(language, t(translationKey), {
        NUM: fileCount,
      }),
    [language, t, fileCount]
  )

  return (
    <ConfirmationDialog
      modalProps={{
        title: t(getModalTitle()),
        body: getModalBody(),
        cancel: tWithFileCount("##.Dialog.Cancel"),
        ok: tWithFileCount("##.Download.Download"),
      }}
      {...rest}
    />
  )
}

export default DownloadHelpDialog
